<template>
  <div class="calculate-editor">
    <!-- 编辑器主体内容 -->
    <div class="editor-body">
      <!-- 公式输入区域 -->
      <formula-editor
        ref="formulaEditor"
        v-model="formulaStr"
        :available-variables="availableVariables"
        :all-function-names="allFunctionNames"
        @input="handleFormulaInput"
      ></formula-editor>

      <!-- 变量和函数面板区域 -->
      <div class="content-section">
        <!-- 变量面板 -->
        <variable-panel
          :categories="availableVariables.filter(item => item.value !== 'none')"
          @select="handleVariableSelect"
        ></variable-panel>

        <!-- 函数面板 -->
        <function-panel
          :categories="availableFunctionCategories"
          :initial-expanded-categories="expandedCategories"
          @select="handleFunctionSelect"
          @hover="handleFunctionHover"
        ></function-panel>

        <!-- 函数详情面板 -->
        <function-detail
          :selected-function="selectedFunction"
        ></function-detail>
      </div>
    </div>

    <!-- 编辑器底部按钮 -->
    <editor-footer
      @cancel="$emit('close')"
      @confirm="confirmFormula"
    ></editor-footer>
  </div>
</template>

<script>
// 导入子组件
import FormulaEditor from './FormulaEditor.vue'
import VariablePanel from './VariablePanel.vue'
import FunctionPanel from './FunctionPanel.vue'
import FunctionDetail from './FunctionDetail.vue'
import EditorFooter from './EditorFooter.vue'

export default {
  name: 'CalculateEditor',
  components: {
    FormulaEditor,
    VariablePanel,
    FunctionPanel,
    FunctionDetail,
    EditorFooter
  },
  
  props: {
    // 可用变量列表
    availableVariables: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ''
    }
  },

  watch: {
    value: {
      handler(newValue) {
        this.selectedFunction = this.availableFunctionCategories[0].functions[0]
        if (this.$refs.formulaEditor) {
          this.$refs.formulaEditor.handleChangeTemplate(newValue)
        }
      },
      immediate: true,
      deep: true
    }
  },

  data() {
    return {
      // 公式字符串
      formulaStr: this.value,

      // 展开的函数分类
      expandedCategories: ['数学函数'],

      // 当前选中的函数
      selectedFunction: null,

      // 函数分类和列表
      availableFunctionCategories: [
        {
          name: '数学函数',
          functions: [
            {
              name: 'SUM',
              syntax: 'SUM(number1, number2, ...numberN)',
              description: '计算所有参数的和。可以接受任意数量的数值参数。',
              example: 'SUM(1, 2, 3) → 6; \nSUM(10, -5, 7) → 12; \nSUM(外销合同_对外报价, 外销合同_对内报价) → 计算两个价格的总和; \nSUM(外销商品_单价 * 外销商品_数量) → 计算所有商品的总价'
            },
            {
              name: 'AVG',
              syntax: 'AVG(number1, number2, ...numberN)',
              description: '计算所有参数的平均值。可以接受任意数量的数值参数。',
              example: 'AVG(1, 2, 3, 4) → 2.5; \nAVG(10, 20, 30) → 20; \nAVG(外销商品_单价) → 计算商品单价的平均值; \nAVG(外销费用明细_费用金额) → 计算平均费用'
            },
            {
              name: 'MAX',
              syntax: 'MAX(number1, number2, ...numberN)',
              description: '返回所有参数中的最大值。可以接受任意数量的数值参数。',
              example: 'MAX(1, 5, 3) → 5; \nMAX(10, -2, 7) → 10; \nMAX(外销商品_库存数量) → 找出库存数量最多的商品; \nMAX(外销合同_签订日期) → 获取最新的合同日期'
            },
            {
              name: 'MIN',
              syntax: 'MIN(number1, number2, ...numberN)',
              description: '返回所有参数中的最小值。可以接受任意数量的数值参数。',
              example: 'MIN(1, 5, 3) → 1; \nMIN(10, -2, 7) → -2; \nMIN(外销商品_成本) → 找出成本最低的商品; \nMIN(外销费用明细_费用金额) → 找出最小费用'
            },
            {
              name: 'COUNT',
              syntax: 'COUNT(value1, value2, ...valueN)',
              description: '统计非空参数的个数。可以接受任意类型的参数，但只会统计非空值。',
              example: 'COUNT(1, 2, 3) → 3; \nCOUNT(1, null, 3) → 2; \nCOUNT(外销合同_合同号) → 统计合同总数; \nCOUNT(外销商品_商品名称) → 统计商品种类数'
            },
            {
              name: 'ROUND',
              syntax: 'ROUND(number, num_digits)',
              description: '将数值四舍五入到指定的小数位数。num_digits为正数时表示小数位数，为负数时表示整数位数。',
              example: 'ROUND(3.14159, 2) → 3.14; \nROUND(123.456, -1) → 120; \nROUND(外销合同_对外报价, 2) → 将价格四舍五入到两位小数; \nROUND(外销商品_利润率 * 100, 1) → 利润率百分比保留一位小数'
            },
            {
              name: 'CEIL',
              syntax: 'CEIL(number)',
              description: '向上取整，返回大于或等于给定数值的最小整数。',
              example: 'CEIL(3.14) → 4; \nCEIL(-1.5) → -1; \nCEIL(外销商品_重量) → 向上取整商品重量; \nCEIL(外销商品_数量 / 10) → 计算需要的包装箱数量'
            },
            {
              name: 'FLOOR',
              syntax: 'FLOOR(number)',
              description: '向下取整，返回小于或等于给定数值的最大整数。',
              example: 'FLOOR(3.99) → 3; \nFLOOR(-1.2) → -2; \nFLOOR(外销商品_体积) → 向下取整商品体积; \nFLOOR(外销合同_总金额 / 1000) → 计算总金额的千元整数部分'
            }
          ]
        }
      ]
    }
  },
  
  computed: {
    // 所有可用函数名列表（用于高亮）
    allFunctionNames() {
      return this.availableFunctionCategories
        .flatMap(category => category.functions)
        .map(func => func.name)
        .sort((a, b) => b.length - a.length) // 长函数名优先匹配
    }
  },
  
  methods: {
    init() {
    },

    // 公式输入变化处理
    handleFormulaInput() {
      this.$emit('input', this.formulaStr)
    },
    
    // 变量选择事件处理
    handleVariableSelect(variableName) {
      if (!this.$refs.formulaEditor) return
      this.$refs.formulaEditor.insertContent(variableName)
    },
    
    // 函数选择事件处理
    handleFunctionSelect(functionName) {
      if (!this.$refs.formulaEditor) return
      this.$refs.formulaEditor.insertFunction(functionName)
    },
    
    // 函数鼠标悬停事件处理
    handleFunctionHover(func) {
      this.selectedFunction = func
    },
    
    // 确认公式
    confirmFormula() {
      this.$emit('confirm', this.formulaStr)
      this.$emit('close')
    }
  },

  mounted() {
    this.init()
  }
}
</script>

<style lang="scss" scoped>
.calculate-editor {
  margin: 0 -20px;
  width: calc(100% + 40px);
  background: #fff;
  border-radius: 6px;
  //box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  .content-section {
    display: flex;
    gap: 20px;
    margin-top: 20px;

    > div {
      flex: 1;
      background: #f5f7fa;
      border-radius: 4px;
      padding: 15px;
    }
  }
}

.picker {
  height: 525px;
  text-align: left;
  width: 50%;
  margin: 0 auto;
  .code-edit {
    height: 240px;
    border-radius: 6px;
    border: 1px solid #e8e9eb;
  }
}
.top-title {
  background-color: #fafafa;
  height: 30px;
  vertical-align: center;
  line-height: 30px;
  padding-left: 10px;
  border-radius: 4px 4px 0 0;
  border-bottom: none;
}
/deep/ .CodeMirror {
  height: 200px !important;
  /*函数样式*/
  .cm-func {
    font-weight: bold;
    color: #ae4597;
    line-height: 14px;
    margin: 0 1px;
    padding: 0 2px;
  }
  .CodeMirror-scroll {
    width: 100%;
  }
}

</style>